import $, { type } from "jquery";
import CommonService from "./common.service";
import ScatterService from "./scatter.service";

class RanksService extends CommonService{
    /***  Series Functions ***/
    getSeries(name,gridIndex,xAxisIndex,yAxisIndex,config = {},isEmotionalNeeds = false){
        let baseSeries = {
            name: name,
            type: 'scatter',
            gridIndex: gridIndex,
            xAxisIndex: xAxisIndex,
            yAxisIndex: yAxisIndex,
            data: []
        };    
        $.extend(true, baseSeries, config);
        return baseSeries;
    }
    getNeedsMetricsGrid(){
        return [{
            left: 80,
            right: 220,
            top: 55,
            containLabel: true,
            show: true,
            backgroundColor: "#F2F2F2",
            borderWidth: 0
        }];
    }
    getAxisLabelRotation(names = []){
        return names.length > 50 ? 90 : 35;
    }
    getNeedsMetricsXAxis(names = []){
        let xAxis = this.getBaseXAxis();
        xAxis.data = names;
        $.extend(true, xAxis.axisLabel, this.getDefaultAxisTextStyleForAnalyticChart());
        $.extend(true, xAxis.axisLabel, {
            rotate: this.getAxisLabelRotation(names),
            fontFamily: "Aptos Narrow",
            fontSize: 9,
            hideOverlap: true,
            interval: 0
        });
        xAxis.splitLine.show = false;
        return [xAxis];
    }
    getNeedsMetricsVerticalLineSeries(names = [], tierBoundaryIndexes = []){
        let fifthItemLines = [];
        for (let index = 5; index < names.length; index += 5) {
            fifthItemLines.push({
                value: [index, 0],
                itemStyle: { color: "#d9d9d9" }
            });
        }
        let tierLines = tierBoundaryIndexes.map(index => ({
            value: [index, 1],
            itemStyle: { color: "#000000" }
        }));
        return {
            name: "category-separators",
            type: "custom",
            excludeFromLegend: true,
            silent: true,
            tooltip: { show: false },
            encode: { x: 0, tooltip: [] },
            z: 4,
            data: fifthItemLines.concat(tierLines),
            renderItem: function(params, api) {
                const boundaryIndex = api.value(0);
                const leftCenter = api.coord([boundaryIndex - 1, 0])[0];
                const rightCenter = api.coord([boundaryIndex, 0])[0];
                const x = (leftCenter + rightCenter) / 2;
                const isTierLine = api.value(1) == 1;
                return {
                    type: "line",
                    shape: {
                        x1: x,
                        y1: params.coordSys.y,
                        x2: x,
                        y2: params.coordSys.y + params.coordSys.height
                    },
                    style: {
                        stroke: api.visual("color"),
                        lineWidth: isTierLine ? 2.5 : 1,
                        lineDash: isTierLine ? [5, 3] : null
                    }
                };
            }
        };
    }
    getYAxisLabelFormatter(formatter = "{value}", hideZero = true, decimalPlaces = null){
        let axisFormatter = formatter == "#" ? "{value}" : formatter;
        return function(value) {
            if (hideZero && Number(value) == 0) {
                return "";
            }
            let formattedValue = decimalPlaces == null ? value : Number(value).toFixed(decimalPlaces);
            return String(axisFormatter).replace("{value}", formattedValue);
        };
    }
    getPrimaryNeedYAxis(name,axisConfiguration = {}){
        let minValue = typeof axisConfiguration.minValue != "undefined" && axisConfiguration.minValue != null && axisConfiguration.minValue != -1 ? axisConfiguration.minValue : null;
        let maxValue = typeof axisConfiguration.maxValue != "undefined" && axisConfiguration.maxValue != null && axisConfiguration.maxValue != -1 ? axisConfiguration.maxValue : null;
        if (typeof axisConfiguration.viewWindow != "undefined" && axisConfiguration.viewWindow != null) {
            minValue = typeof axisConfiguration.viewWindow.min != "undefined" && axisConfiguration.viewWindow.min != null ? axisConfiguration.viewWindow.min : minValue;
            maxValue = typeof axisConfiguration.viewWindow.max != "undefined" && axisConfiguration.viewWindow.max != null && axisConfiguration.viewWindow.max != -1 ? axisConfiguration.viewWindow.max : maxValue;
        }
        let yAxis = this.getYAxisForNeed(minValue, maxValue);
        if (axisConfiguration.minValue == -1){
            yAxis.min = null;
        }
        if (axisConfiguration.maxValue == -1){
            yAxis.max = null;
        }
        yAxis.name = name;
        yAxis.nameTextStyle = {
            fontFamily: "Franklin Gothic Demi Cond",
            fontSize: 10.5
        };
        let isValueGapAxis = String(name).trim().toLowerCase() == "value gap";
        yAxis.inverse = isValueGapAxis
            ? true
            : (typeof axisConfiguration.inverse != "undefined" ? axisConfiguration.inverse : false);
        if (isValueGapAxis) {
            yAxis.interval = 0.1;
            yAxis.nameLocation = "start";
            yAxis.min = 0;
            yAxis.max = null;
        }
        let formatter = axisConfiguration.formatter || axisConfiguration.format || "{value}";
        yAxis.axisLabel.formatter = this.getYAxisLabelFormatter(formatter, !isValueGapAxis, isValueGapAxis ? 1 : null);
        yAxis.splitLine.show = true;
        yAxis.splitLine.lineStyle = {
            color: "#FFFFFF",
            width: 1
        };
        return yAxis;
    }
    getSecondaryMetricYAxis(axisName = "Metric", axisConfiguration = {}){
        let minValue = typeof axisConfiguration.minValue != "undefined" && axisConfiguration.minValue != null && axisConfiguration.minValue != -1 ? axisConfiguration.minValue : null;
        let maxValue = typeof axisConfiguration.maxValue != "undefined" && axisConfiguration.maxValue != null && axisConfiguration.maxValue != -1 ? axisConfiguration.maxValue : null;
        if (typeof axisConfiguration.viewWindow != "undefined" && axisConfiguration.viewWindow != null) {
            minValue = typeof axisConfiguration.viewWindow.min != "undefined" && axisConfiguration.viewWindow.min != null ? axisConfiguration.viewWindow.min : minValue;
            maxValue = typeof axisConfiguration.viewWindow.max != "undefined" && axisConfiguration.viewWindow.max != null ? axisConfiguration.viewWindow.max : maxValue;
        }
        let yAxis = this.getYAxisForNeed(minValue, maxValue);
        if (axisConfiguration.minValue == -1){
            yAxis.min = null;
        }
        if (axisConfiguration.maxValue == -1){
            yAxis.max = null;
        }
        yAxis.name = axisName;
        yAxis.nameTextStyle = {
            fontFamily: "Franklin Gothic Demi Cond",
            fontSize: 10.5
        };
        yAxis.position = "right";
        yAxis.inverse = typeof axisConfiguration.inverse != "undefined" ? axisConfiguration.inverse : false;
        let formatter = axisConfiguration.formatter || axisConfiguration.format || "{value}";
        yAxis.axisLabel.formatter = this.getYAxisLabelFormatter(formatter);
        yAxis.splitLine.show = false;
        return yAxis;
    }
    getAverageMarkLine(value, color, name = "Average"){
        if (value === null || typeof value == "undefined" || isNaN(value)) {
            return {
                symbol: "none",
                label: { show: false },
                data: []
            };
        }
        return {
            symbol: "none",
            label: {
                show: false,
                color: color
            },
            lineStyle: {
                color: color,
                type: "dotted",
                width: 2
            },
            data: [{ yAxis: Number(value) }]
        };
    }
    getAverageLineSeries(name, averageValue, color, yAxisIndex = 0, numberOfValues = 0){
        if (averageValue === null || typeof averageValue == "undefined" || isNaN(averageValue)) {
            return null;
        }
        return {
            name: name,
            type: "line",
            xAxisIndex: 0,
            yAxisIndex: yAxisIndex,
            gridIndex: 0,
            symbol: "none",
            silent: true,
            lineStyle: {
                color: color,
                type: "dotted",
                width: 2
            },
            itemStyle: {
                color: color
            },
            label: {
                show: false
            },
            data: Array(numberOfValues).fill(Number(averageValue))
        };
    }
    getZeroPercentMarkLine(values = []){
        let numericValues = values.filter(value => !isNaN(value)).map(value => Number(value));
        let hasPositive = numericValues.some(value => value > 0);
        let hasNegative = numericValues.some(value => value < 0);
        if (!hasPositive || !hasNegative) {
            return {};
        }
        return {
            data: [{
                yAxis: 0,
                lineStyle: {
                    color: "#333333",
                    type: "solid",
                    width: 2
                },
                label: {
                    show: true,
                    formatter: "0%",
                    color: "#000",
                    fontSize: 10.5,
                    fontFamily: "Franklin Gothic Demi Cond",
                }, 
            }]
        };
    }
    getNeedCircleSeries(name, values = [], averageValue = null, averageColor = "#2F5EB3"){
        return {
            name: name,
            type: "scatter",
            xAxisIndex: 0,
            yAxisIndex: 0,
            gridIndex: 0,
            symbol: "circle",
            symbolSize: 5.5,
            itemStyle: {
                color: averageColor
            },
            data: values
        };
    }
    formatNeedsMetricsRankValue(seriesName, valueToDisplay, seriesIndex = 0, axisConfiguration = {}){
        let formattedValue;
        if (seriesName.endsWith(" Avg")) {
            formattedValue = Number(valueToDisplay).toFixed(2);
        } else if (seriesName == "Value Gap" || seriesName == "Satisfaction Gap Size" || seriesName == "Emotional Gap Size" || seriesName == "Importance Average") {
            formattedValue = Number(valueToDisplay).toFixed(3);
        } else if (seriesName == "n=") {
            formattedValue = Number(valueToDisplay).toFixed(0);
        } else if (seriesName == "Willingness by Need") {
            formattedValue = Number(valueToDisplay).toFixed(2);
        } else {
            formattedValue = Number(valueToDisplay);
        }

        let axisFormatter = axisConfiguration.formatter || axisConfiguration.format;
        if (typeof axisFormatter != "undefined" && axisFormatter != null && axisFormatter != "") {
            axisFormatter = axisFormatter == "#" ? "{value}" : axisFormatter;
            return String(axisFormatter).replace("{value}", formattedValue);
        }

        if (seriesName == "Willingness by Need") {
            return formattedValue + "%";
        }
        let suffix = seriesIndex == 1 && !seriesName.includes("Growth") ? "" : "%";
        return formattedValue + suffix;
    }
    getMetricBarSeries(name, values = [], averageValue = null, averageColor = "#c8c1aa", axisConfiguration = {}){
        let vm = this;
        return {
            name: name,
            type: "bar",
            xAxisIndex: 0,
            yAxisIndex: 1,
            gridIndex: 0,
            barMaxWidth: 28,
            label: {
                align: "center",
                verticalAlign: "middle",
                offset: [1,-13],
                fontFamily: "Aptos Narrow",
                fontSize: 9,
                show: true,
                position: "insideBottom",
                rotate: 90,
                formatter: function(params) {
                    return !isNaN(params.value) ? vm.formatNeedsMetricsRankValue(params.seriesName, params.value, 1, axisConfiguration) : "";
                }
            },
            itemStyle: {
                color: averageColor
            },
            markLine: {
                symbol: "none",
                label: { show: false },
                data: []
            },
            data: values
        };
    }
    /*** yAxis Functions ***/
    getMarkAreaData(){
        return [
            [
              {
                  itemStyle: {
                  color: "#f2dcdb"
                  }
              },            
              {
                  yAxis: 10
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#FFFFC5"
                  },
                  yAxis: 10
              },            
              {
                  yAxis: 35
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#FAFBF7"
                  },
                  yAxis: 35
              },            
              {
                  yAxis: 75
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#EBF1DE"
                  },
                  yAxis: 75
              },            
              {
                  yAxis: 90
              }
            ],
            [
              {
                  itemStyle: {
                  color: "#D8E4BC"
                  },
                  yAxis: 90
              },            
              {
                  yAxis: 100
              }
            ]
        ];
      }
  
/*Tooltip Functions */
toolTipForRanksChart = (params) => {
    let returnValue = "";
    for (let i = 0; i < params.length; ++i) {
        let valueToDisplay = params[i].value;
        let seriesName = params[i].seriesName;
        //returnValue += params[i].marker += " ";
        returnValue += params[i].seriesName += " ";
        returnValue += "<span style='text-align:right'><b>" 
        if (valueToDisplay > 0){
           returnValue += Number(valueToDisplay).toFixed(1) + "%";
        } else {
            returnValue += "--";
        }
        returnValue += "</b></span>";
        returnValue += "<br>";
    }
    return returnValue;
  }
toolTipForNeedsMetricsRankChart = (params, primaryAxisConfiguration = {}, metricAxisConfiguration = {}, metricSeriesName = "") => {
    let returnValue = "";
    if (params.length > 0) {
        returnValue += "<b>" + params[0].axisValue + "</b><br>";
    }
    for (let i = 0; i < params.length; ++i) {
        let valueToDisplay = params[i].value;
        returnValue += params[i].marker + " " + params[i].seriesName + " ";
        returnValue += "<span style='text-align:right'><b>";
        if (!isNaN(valueToDisplay)) {
            let axisConfiguration = params[i].seriesName == metricSeriesName || params[i].seriesName == metricSeriesName + " Avg"
                ? metricAxisConfiguration
                : primaryAxisConfiguration;
            returnValue += this.formatNeedsMetricsRankValue(params[i].seriesName, valueToDisplay, i, axisConfiguration);
        } else {
            returnValue += "--";
        }
        returnValue += "</b></span><br>";
    }
    return returnValue;
  }
}
export default new RanksService();             
